import useAxiosInterceptor from 'src/services/interceptor';
import { canDo, FormSubmissionSignatureContext, SignatureAction } from 'src/types/signatureContext';
import { physician, SIGNING_PIN_HEADER } from '../constants';

// these come from the front-ent (document-management-constants.ts)
export type DocumentStatus =
  | 'AWAITING SIGNATURE'
  | 'NEEDS APPROVED'
  | 'UPCOMING'
  | 'URGENT'
  | 'OVERDUE'
  | 'COMPLETED';
// these come from the API server (SignatureStatus.java)
export type DocumentSignatureStatus = 'PENDING' | 'COMPLETE';
// this is a set made for the supervisor signature workflow
export type DocumentSignatureWorkflowStatuses = 'PENDING_APPROVAL' | 'APPROVED' | 'SIGNED';
// this is a composite set for use displaying as fulls tatus information as possible
export type DocumentSignatureCompositeStatuses =
  | ''
  | DocumentStatus
  | DocumentSignatureWorkflowStatuses
  | 'NEEDS APPROVED';

export function compositeWorkflowStatusFromDocument(
  document: PhysicianDocument
): DocumentSignatureCompositeStatuses {
  if (!document) return '';

  // Prefer signatureContext.physician.signStatus when available (BE-owned derivation
  // post-THIN-7160); fall back to the legacy supervisorStatus column which held the
  // physician sig state pre-THIN-7148. The 'COMPLETE' branch covers the legacy enum
  // value, 'COMPLETED' covers signatureContext (which uses the canonical SignatureStatus
  // enum name).
  const physicianSignStatus =
    document.signatureContext?.physician?.signStatus ?? document.supervisorStatus;

  // When the BE still exposes CAN_SIGN, the physician workflow is not finished —
  // even if physician.signStatus is stale (e.g. COMPLETED with signedAt null).
  const physicianCanSign = canDo(
    document.signatureContext?.allowedPhysicianSignatureActions,
    SignatureAction.CAN_SIGN
  );

  if (
    !physicianCanSign &&
    (physicianSignStatus === 'COMPLETED' || physicianSignStatus === 'COMPLETE')
  ) {
    return 'SIGNED';
  }
  // THIN-7218: physician approval moved off approvedForSupervisorSignature onto its own
  // approvedForPhysicianSignature column.
  const physicianApproved = document.approvedForPhysicianSignature;
  if (physicianSignStatus === 'PENDING' && physicianApproved) {
    return 'APPROVED';
  }

  if (document.status === 'COMPLETED') return 'PENDING_APPROVAL';
  if (document.status === 'OVERDUE') return 'OVERDUE';
  if (document.status === 'URGENT') return 'URGENT';
  if (document.status === 'UPCOMING') return 'UPCOMING';
  if (document.status === 'AWAITING SIGNATURE') return 'NEEDS APPROVED';
  return '';
}

export interface PhysicianDTO {
  patientIds: Array<string> | [];
  facilityIds: Array<string> | [];
  type: Array<string> | [];
  startDateOfService: string | null;
  endDateOfService: string | null;
  startDueDate: string | null;
  endDueDate: string | null;
  status: string | null;
  sortBy: 'facilityName' | 'patientName' | 'documentType' | 'dateOfService' | 'dueDate';
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export interface PhysicianDocument {
  uuid: string;
  providerId: string | null;
  episodeOfCareId: string | null;
  type: string | null;
  dueDate: string | null;
  lastUpdatedOn: string | null;
  status: string;
  supervisorStatus: string | null;
  documentType: string | null;
  dueDateToSign: string | null;
  signees: string | null;
  signedBy: string | null;
  pdfUrl: string | null;
  isForSupervisor: string | null;
  supervisorId: string | null;
  supervisorName: string | null;
  physicianId: string | null;
  physicianName: string | null;
  patientName: string | null;
  lastUpdatedOnString: string | null;
  dueDateString: string | null;
  forSign: string | null;
  mailSentToPhysician: string | null;
  formType: string | null;
  patientId: string | null;
  facilityName: string | null;
  dailySessionNoteUuid: string | null;
  isReassigned: string | null;
  dateOfService: string | null;
  visits: string | null;
  payerName: string | null;
  sectionGg: string | null;
  documentKey: string | null;
  rawDataS3Url: string | null;
  facilityId: string | null;
  documentUrl: string | null;
  reformId: string | null;
  approvedForSupervisorSignature: boolean | null;
  // THIN-7218: physician-flow approval flag, split from approvedForSupervisorSignature.
  approvedForPhysicianSignature: boolean | null;
  signatureContext?: FormSubmissionSignatureContext;
}

export const initialPhysicianDTO: PhysicianDTO = {
  patientIds: [],
  facilityIds: [],
  type: [],
  startDateOfService: null,
  endDateOfService: null,
  startDueDate: null,
  endDueDate: null,
  status: null,
  sortBy: 'facilityName',
  sortDirection: 'desc',
  page: 0,
  pageSize: 20,
};

class PhysicianService {
  private axiosInstance;

  constructor() {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- factory, not a React hook (matches npcLogService)
    this.axiosInstance = useAxiosInterceptor();
  }

  getAllPhysicianDocuments = (physicianId: string, physicianDto: PhysicianDTO) => {
    return this.axiosInstance.post(physician.getAllDocumentsOfPhysician(physicianId), physicianDto);
  };

  getAllFacility = (payload: any) => {
    return this.axiosInstance.get(`${physician?.facilities}?active=true`, {
      params: {
        ...payload,
      },
    });
  };

  getAllPatient = (payload: any) => {
    return this.axiosInstance.get(`${physician?.getAllPatient}`, {
      params: {
        ...payload,
      },
    });
  };

  signPhysicianDocument = (payload: any) => {
    //  THIN-7775: strip any PIN out of the query params and send it as SIGNING_PIN_HEADER.
    const { pin, ...params } = payload ?? {};
    return this.axiosInstance.post(`/provider/signature/sign-document`, null, {
      params,
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
        ...(pin ? { [SIGNING_PIN_HEADER]: pin } : {}),
      },
    });
  };

  bulkSignPhysicianDocuments = (
    physicianId: string,
    documentIds: string[],
    signatureStatus: string,
    signerName: string,
    consentConfirmation = false,
    pin?: string
  ) => {
    return this.axiosInstance.post(
      `/provider/signature/bulk-signature/physician/${physicianId}`,
      null,
      {
        params: {
          documentIds: documentIds,
          signatureStatus,
          signerName,
          consentConfirmation,
        },
        headers: {
          'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
          //  THIN-7681: signing PIN (verified at the controller boundary) when the flag is on.
          //  THIN-7775: as a header, not a query param — query strings reach the access logs.
          ...(pin ? { [SIGNING_PIN_HEADER]: pin } : {}),
        },
        // axios v1 types paramsSerializer's argument as the request body shape; widen it back to a
        // string-keyed map so the for-in indexing below stays valid (matches axios 0.26 behavior).
        paramsSerializer: (params: Record<string, any>) => {
          // Create URL-encoded string with [] notation for arrays
          const parts = [];
          for (const key in params) {
            if (Array.isArray(params[key])) {
              params[key].forEach((val: any) => {
                parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
              });
            } else {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
            }
          }
          return parts.join('&');
        },
      }
    );
  };

  updateDocumentStatuses = (
    physicianId: string,
    physicianDocumentUuids: PhysicianDocument['uuid'][],
    physicianSignatureStatus: DocumentSignatureWorkflowStatuses,
    physicianSignature: string | null,
    pin?: string,
    //  Consent is recorded per signature, so it must carry the signer's actual answer to the
    //  "legally binding" checkbox rather than a constant. Only the SIGNED path attests.
    consentConfirmation = false
  ) => {
    // Use the new bulk signature endpoint when signing documents
    if (physicianSignatureStatus === 'SIGNED') {
      return this.bulkSignPhysicianDocuments(
        physicianId,
        physicianDocumentUuids,
        physicianSignatureStatus,
        physicianSignature || '',
        consentConfirmation,
        pin
      );
    }

    // Use the original endpoint for other status updates (like APPROVED)
    return this.axiosInstance.put(physician.updatePhysicianDocumentStatuses(physicianId), null, {
      params: {
        documentIds: physicianDocumentUuids.join(','),
        signatureStatus: physicianSignatureStatus,
        signatureName: physicianSignature,
      },
    });
  };
}

const physicianService = new PhysicianService();

Object.freeze(physicianService);

export default physicianService;
